import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import styled, { keyframes } from 'styled-components';
import { ThemeProvider } from 'styled-components';
import { theme } from '../../../styles/theme';
import config from '../../../config/environment';

// Removed falling animation for professional look

const Container = styled.div`
  min-height: 100vh;
  padding-top: 120px;
  background: #101d40;
  position: relative;
`;

const NewsletterContainer = styled.div`
  background: #101d40;
  padding: 4rem 2rem;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  position: relative;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  font-family: ${props => props.theme.fonts.main};
  margin: 2rem;
  
  @media (max-width: 1024px) {
    padding: 3rem 1.5rem;
    margin: 1rem;
  }
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
    margin: 0.5rem;
    min-height: 70vh;
  }
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  width: 100%;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 4rem;
  margin: 0 0 1rem 0;
  letter-spacing: 0.5px;
  font-weight: 700;
  color: white;
  text-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  margin: 0 0 2rem 0;
  letter-spacing: 0.3px;
  font-weight: 400;
  color: ${props => props.theme.colors.accent};
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Description = styled.p`
  font-size: 1.3rem;
  line-height: 1.6;
  margin: 0 0 3rem 0;
  color: rgba(255, 255, 255, 0.9);
  font-family: ${props => props.theme.fonts.body};
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 500px;
  margin: 0 auto;
  position: relative;
`;

const InputGroup = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const Input = styled.input`
  padding: 1.2rem 1.5rem;
  width: 100%;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  font-family: ${props => props.theme.fonts.main};
  backdrop-filter: blur(5px);
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
    font-family: ${props => props.theme.fonts.main};
  }
  
  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.5);
    background-color: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  }
`;

const SubmitButton = styled.button`
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  padding: 1.2rem 3rem;
  border-radius: 12px;
  cursor: pointer;
  font-weight: bold;
  font-size: 1.1rem;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  font-family: ${props => props.theme.fonts.main};
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  text-transform: uppercase;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
    opacity: 0.9;
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusMessage = styled.div`
  position: absolute;
  bottom: -50px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 1rem;
  color: ${props => props.error ? '#ff6b6b' : '#51cf66'};
  transition: all 0.3s ease;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
`;

const FeaturesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin: 3rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin: 2rem 0;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  text-align: left;
  font-family: ${props => props.theme.fonts.body};
  
  &::before {
    content: '✨';
    font-size: 1.5rem;
    flex-shrink: 0;
  }
`;

const DuchessCollectionNewsletter = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [status, setStatus] = useState({
    submitting: false,
    error: null,
    success: false,
    status: null
  });

  // Removed animation state for professional look

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, error: null, success: false, status: null });

    try {
      const response = await fetch(`${config.apiEndpoint}/newsletter/v1/api/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({
          ...formData,
          origin: window.location.origin,
          tags: ['Duchess Collection']
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Subscription failed');
      }

      const data = await response.json();

      if (data.success === false) {
        setStatus({
          submitting: false,
          error: data.message,
          success: false,
          status: data.status
        });
        return;
      }

      setStatus({
        submitting: false,
        error: null,
        success: true,
        status: data.status
      });
      
      if (data.status === 'new_subscription') {
        setFormData({ firstName: '', lastName: '', email: '' });
      }
    } catch (error) {

      setStatus({
        submitting: false,
        error: error.message || 'An error occurred while subscribing. Please try again.',
        success: false,
        status: null
      });
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container>
        <Helmet>
          <title>Duchess Collection Newsletter | Belvoir Castle Duchess Collection</title>
          <meta name="description" content="Sign up for the Belvoir Castle Duchess Collection newsletter to receive updates about luxury homeware, exclusive collections, and special offers." />
          <meta name="robots" content="noindex, nofollow" />
          <link rel="canonical" href="https://www.belvoircastle.com/duchess-collection/newsletter" />
        </Helmet>
        
        <NewsletterContainer>
          <ContentWrapper>
            <Title>Duchess Collection Newsletter</Title>
            <Subtitle>Luxury Homeware & Exclusive Collections</Subtitle>
            <Description>
              Stay connected with the latest from Belvoir Castle Duchess Collection. Get exclusive updates on luxury homeware, new collections, special offers, and curated pieces delivered straight to your inbox.
            </Description>
            
            <FeaturesList>
              <FeatureItem>Exclusive luxury homeware updates</FeatureItem>
              <FeatureItem>New collection announcements</FeatureItem>
              <FeatureItem>Special offers and discounts</FeatureItem>
              <FeatureItem>Curated style inspiration</FeatureItem>
            </FeaturesList>
            
            <FormContainer onSubmit={handleSubmit}>
              <InputGroup>
                <Input 
                  type="text" 
                  name="firstName"
                  placeholder="First Name" 
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
                <Input 
                  type="text" 
                  name="lastName"
                  placeholder="Last Name" 
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </InputGroup>
              <Input 
                type="email" 
                name="email"
                placeholder="Email Address" 
                value={formData.email}
                onChange={handleChange}
                required
              />
              <SubmitButton 
                type="submit" 
                disabled={status.submitting}
              >
                {status.submitting ? 'SIGNING UP...' : 'JOIN DUCHESS COLLECTION NEWSLETTER'}
              </SubmitButton>
              
              {status.error && (
                <StatusMessage error>{status.error}</StatusMessage>
              )}
              {status.success && (
                <StatusMessage>
                  {status.status === 'already_subscribed' 
                    ? 'You are already subscribed to our Duchess Collection newsletter!'
                    : status.status === 'pending_confirmation'
                      ? 'Thank you for subscribing! Please check your email to confirm your subscription.'
                      : status.status === 'new_subscription'
                        ? 'Welcome to the Duchess Collection newsletter! You have been successfully subscribed.'
                        : 'Thank you for subscribing to our Duchess Collection newsletter!'}
                </StatusMessage>
              )}
            </FormContainer>
          </ContentWrapper>
        </NewsletterContainer>
      </Container>
    </ThemeProvider>
  );
};

export default DuchessCollectionNewsletter;